import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import SearchPanel from "../../components/SearchPanel";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from '@fullcalendar/daygrid'
/* CSS imports */
// import '@fullcalendar/core/main.css'
// import '@fullcalendar/daygrid/main.css'
import './style.less'
import { Divider, Form, Label, Popup, Icon } from "semantic-ui-react";
import HotelsDropDown from "../../components/shared/HotelsDropDown";
import AllocationsWithDatesDropDown from "../../components/shared/AllocationsWithDatesDropDown";
import { useTextResources, useMessageResources } from "../../language";
import RoomTypesDropDown from "../../components/shared/RoomTypesDropDown";
import RatesDropDown from "../../components/shared/RatesDropDown";
import { Monthly } from "../../api/services/next";
import moment from 'moment'
import { MonthlySideMenu } from "../../components/MonthlySideMenu";
import { useCurrentHotel } from "../../hooks/useCurrentHotel";
import _ from 'lodash'
import { RestrictionTypesValues } from "../../components/shared/constants";
import CheckboxWrapper from "../../components/shared/Checkbox";
import SegmentedToggle from "../../components/shared/SegmentedToggle";
import DayCell, { useDayCellBuckets, useEventSpans, useAllocationDateBuckets } from "./DayCell";
import FilterChips from "./FilterChips";
import PackagesDropDown from "../../components/shared/PackagesDropDown";
import GridDayCell, { roomTypeLineName } from "./GridDayCell";
import { useMonthlyFlow } from "./monthlyFlow";

// Stable reference: DropDown refetches whenever `data` changes identity.
const NO_PACKAGES = [];

const TaxToggle = ({ value, onChange, disabled, ...rest }) => {
    const text = useTextResources();
    // SearchPanel clones this field's element on every render and always overwrites `value`
    // with its own internal `values` state (frozen at SearchPanel's mount time) — the `value`
    // prop we receive here can be stale and does NOT reflect a later currentHotel change.
    // `disabled` is untouched by that override, so when locked, force the display to After
    // Tax regardless of the (possibly stale) incoming value — this is the only prop we can
    // trust here for the lock's own correctness.
    const effectiveValue = disabled ? true : !!value;
    const toggle = (
        <SegmentedToggle
            options={[
                { label: text("BeforeTax") || "Before Tax", value: false },
                { label: text("AfterTax") || "After Tax", value: true },
            ]}
            value={effectiveValue}
            onChange={onChange}
            disabled={disabled}
        />
    );
    return (
        <Form.Field {...rest}>
            <label>{text("PriceDisplay") || "Price Display"}</label>
            {disabled
                ? <Popup trigger={<span>{toggle}</span>} content={text("PriceDisplayTaxInclusiveHotel") || "This hotel's prices already include tax"} />
                : toggle}
        </Form.Field>
    );
};

const AvailableToggle = ({ value, onChange, ...rest }) => {
    const text = useTextResources();
    return (
        <Form.Field {...rest}>
            <label>{text("DisplayMode") || "Display"}</label>
            <SegmentedToggle
                options={[
                    { label: text("Available") || "Available", value: true },
                    { label: text("Occupied") || "Occupied", value: false },
                ]}
                value={value !== undefined ? value : true}
                onChange={onChange}
            />
        </Form.Field>
    );
};

export const MonthlyView = () => {
    const text = useTextResources()
    const message = useMessageResources()
    const currentHotel = useCurrentHotel()
    const [dates, setDates] = useState([])
    const [hotelId, setHotelId] = useState(currentHotel?.Id)
    const [roomTypeId, setRoomTypeId] = useState()
    const [rateId, setRateId] = useState(currentHotel?.HotelParameter?.PrimaryRateID)
    // Read via SearchPanel's own field-value tracking (keyed by Form.Field's key="PackageId"
    // below), not via this state — kept only so `setPackageId` can reset the field on hotel change.
    const [_packageId, setPackageId] = useState()
    const [allocationIds, setAllocationIds] = useState([])
    const [allocationDates, setAllocationDates] = useState([]);
    const [availableMode, setAvailableMode] = useState(true)
    // The tax lock must follow whichever hotel is actually selected in this screen's own
    // Hotel field (hotelId), not just the session-wide currentHotel — a user can pick a
    // different hotel here without that changing their global current hotel. `null` means
    // "nothing explicitly picked yet" — until the user touches the Hotel field, fall back to
    // (and stay reactive to) currentHotel; onHotelItemChange below sets this once a specific
    // hotel is picked, and that selection then takes priority.
    const [selectedHotelParameter, setSelectedHotelParameter] = useState(null)
    const pricesIncludeTax = !!(selectedHotelParameter ?? currentHotel?.HotelParameter)?.IsProductPricingIncludesTax
    const [afterTax, setAfterTax] = useState(pricesIncludeTax)
    const [data, setData] = useState({
        Availability: [],
        Yield: [],
        Products: [],
        Discounts: [],
        Restrictions: [],
        Forecast: [],
        Overbooking: [],
    })
    const [state, setState] = useState({})
    const doSearchRef = useRef()
    const changeFieldRef = useRef()
    const containerRef = useRef()
    const [filterExpanded, setFilterExpanded] = useState(false)
    const [selectedDate, setSelectedDate] = useState(null)
    const [containerHeight, setContainerHeight] = useState(null)
    const [roomTypeName, setRoomTypeName] = useState('')
    const [rateName, setRateName] = useState('')
    const [packageName, setPackageName] = useState('')

    // HS-70186: additive Monthly Grid flow. `flow` is "legacy" (today's exact code
    // path, below) or "grid" (server-built cells via GetMonthlyGrid). See monthlyFlow.js.
    const { flow, settingsLoaded, onGridNotFound } = useMonthlyFlow()
    const [gridResult, setGridResult] = useState(null)
    const [gridRoomTypeId, setGridRoomTypeId] = useState(null)
    // Stale-response guard: the spec object of the latest grid request. SearchPanel
    // hands the same object to the api and to onSearchComplete, so identity tells a
    // superseded response (earlier month/filter) from the current one.
    const latestGridSpecRef = useRef(null)

    useEffect(() => {
        const measure = () => {
            if (containerRef.current) {
                const top = containerRef.current.getBoundingClientRect().top;
                setContainerHeight(window.innerHeight - top);
            }
        };
        measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, [filterExpanded])

    const onHotelItemChange = useCallback((item) => {
        setSelectedHotelParameter(item?.HotelParameter ?? null);
    }, []);
    const onRoomTypeItemChange = useCallback((item) => {
        setRoomTypeName(item?.Name || '');
    }, []);
    const onRateItemChange = useCallback((item) => {
        setRateName(item?.Name || '');
    }, []);
    const onPackageItemChange = useCallback((item) => {
        setPackageName(item?.Name || '');
    }, []);

    useEffect(() => {
        setPackageId(undefined);
        setPackageName('');
        changeFieldRef.current?.('PackageId', null);
    }, [roomTypeId, rateId]);

    const onAllocationDatesItemsChange = (allocations) => {
        setAllocationDates(allocations.flatMap(a => a.AllocationDates));
    }

    const roomTypesSpec = useMemo(() => ({
        HotelId: hotelId
    }), [hotelId])
    const ratesSpec = useMemo(() => ({
        HotelId: hotelId,
        RoomTypeId: roomTypeId
    }), [hotelId, roomTypeId])
    // HS-70186: the Package field is disabled until a rate is picked, so without one
    // it gets a fixed empty list instead of a GetPackages call on every page load.
    const packagesSpec = useMemo(() => ({
        HotelId: hotelId,
        RateId: rateId,
        RoomTypeId: roomTypeId,
        IsActive: true
    }), [hotelId, rateId, roomTypeId])
    const allocationsSpec = useMemo(() => ({
        HotelId: hotelId,
        RoomTypeIds: roomTypeId ? [roomTypeId] : [],
        RateIds: rateId ? [rateId] : [],
        Ids: allocationIds
    }), [hotelId, roomTypeId, rateId, allocationIds])
    // HS-70186 (grid flow only): the legacy inline object literal below is recreated
    // every render, which re-triggers the allocations dropdown's fetch on every
    // render, not just when the filter actually changes (the refetch-storm bug this
    // ticket measured at 2-4 concurrent GetAllocationsWithDates calls per page load).
    // Memoized here so the grid flow makes exactly one call per real filter/date
    // change; the legacy flow keeps the exact unmemoized literal, untouched.
    const gridAllocationsSpec = useMemo(() => ({
        ...allocationsSpec,
        IsStrategy: false,
        FromDate: moment(dates[0]).toISOString(true),
        ToDate: moment(dates[1]).toISOString(true)
    }), [allocationsSpec, dates])

    // HotelsDropDown's own default spec only sets IsActive/IncludeOnlyHotelsWithUserPermissions
    // and is replaced (not merged) by any spec prop passed in — replicate both here alongside
    // Includes so onHotelItemChange can read the picked hotel's HotelParameter.
    const hotelsSpec = useMemo(() => ({
        IsActive: true,
        IncludeOnlyHotelsWithUserPermissions: true,
        Includes: ['HotelParameter'],
    }), [])

    // HS-70186: a 404 (flag flipped off mid-session, or a gateway without the new
    // controller deployed yet) resets the flow to legacy so the next fetch (triggered
    // by the search effect below) goes to the endpoint that's actually there. Handled
    // here rather than rethrown, so the expected fallback doesn't surface as an error toast.
    const gridApiWithFallback = useCallback(async (spec) => {
        latestGridSpecRef.current = spec;
        try {
            return await Monthly.GetMonthlyGrid(spec);
        } catch (err) {
            if (err?.status === 404) {
                console.warn("MonthlyView: GetMonthlyGrid returned 404, falling back to legacy flow");
                onGridNotFound();
                return null;
            }
            throw err;
        }
    }, [onGridNotFound]);

    const options = {
        fields: [
            <Form.Field key="HotelId" spec={hotelsSpec} placeholder={text('Select')} value={hotelId} onChange={setHotelId} onChangeItem={onHotelItemChange} control={HotelsDropDown} label={text('Hotel')} />,
            <Form.Field key="RoomTypeId" width={3} label={text("RoomType")} spec={roomTypesSpec} onChange={setRoomTypeId} onChangeItem={onRoomTypeItemChange} placeholder={text('Select')} clearable search control={RoomTypesDropDown} />,
            <Form.Field key="RateId" width={3} label={text("Rate")} spec={ratesSpec} onChange={setRateId} onChangeItem={onRateItemChange} placeholder={text('Select')} clearable search control={RatesDropDown} />,
            <Form.Field key="PackageId" width={3} label={text("Package") || "Package"} spec={packagesSpec} data={rateId ? undefined : NO_PACKAGES} onChange={setPackageId} onChangeItem={onPackageItemChange} placeholder={text('Select')} clearable search control={PackagesDropDown} disabled={!rateId} />,
            <Form.Field key='AllocationId'
                label={text("Allocation")}
                control={AllocationsWithDatesDropDown}
                onChangeItem={onAllocationDatesItemsChange}
                onChange={setAllocationIds}
                multiple
                // HS-70186: grid flow uses the new Monthly.GetMonthlyAllocations endpoint (same
                // shape as GetAllocationsWithDates); legacy flow keeps the default (undefined
                // here, so AllocationsWithDatesDropDown falls back to its own default api).
                {...(flow === 'grid' ? { api: Monthly.GetMonthlyAllocations } : {})}
                // No spec (so no fetch) until the flow is known - otherwise the legacy
                // GetAllocationWithDates fires before the grid flow takes over.
                spec={!settingsLoaded
                    ? null
                    : flow === 'grid'
                        ? gridAllocationsSpec
                        /* legacy: exact current code path - recreated every render, intentionally untouched */
                        : { ...allocationsSpec, IsStrategy: false, FromDate: moment(dates[0]).toISOString(true), ToDate: moment(dates[1]).toISOString(true) }}
                search
                clearable
            />,
            // SearchPanel clones this element and overwrites `value` with its own internal
            // state on every render, so the value passed here only matters as the initial
            // snapshot SearchPanel seeds from `options.initial.AfterTax` below — TaxToggle
            // itself derives the locked display from `disabled`, not from this prop.
            <Form.Field key='AfterTax' control={TaxToggle} value={afterTax} onChange={setAfterTax} disabled={pricesIncludeTax} />,
            <Form.Field key='AvailableMode' control={AvailableToggle} value={availableMode} onChange={setAvailableMode} />,
            <Form.Field key='IncludeReservationPolicy' control={CheckboxWrapper} label={text("ShowReservationPolicy")} />,
        ],
        setDoSearchRef: searchRef => doSearchRef.current = searchRef,
        setChangeFieldRef: ref => changeFieldRef.current = ref,
        // HS-70186: legacy branch keeps calling GetMonthlyData exactly as before; the
        // grid branch calls the new, additive GetMonthlyGrid endpoint. Request shape
        // (initial/spec below) is identical for both.
        api: flow === 'grid' ? gridApiWithFallback : Monthly.GetMonthlyData,
        initial: {
            HotelId: hotelId,
            // RateId filters Prices and Restrictions. Legacy flow only: when the rate has allocations,
            // Availability/Occupancy show allocation left-rooms. The grid flow's hotel numbers never take it.
            RateId: rateId,
            AfterTax: pricesIncludeTax
        },
        spec: {
            FromDate: dates[0],
            ToDate: dates[1]
        }
    }

    // HS-70186: no search until the flow is resolved - searching on mount would send the
    // legacy GetMonthlyData first (and, with the flag off, a second identical one once
    // settings arrive). Afterwards: one search per range change or flow change (404 fallback).
    useEffect(() => {
        if (settingsLoaded && dates && dates.length) {
            doSearchRef.current?.()
        }
    }, [dates, flow, settingsLoaded])

    const datesRender = info => {
        setDates([info.start, info.end])
    }

    const eventColorMap = useMemo(() => {
        const map = {};
        const params = data?.HotelParameters;
        if (!params) return map;
        params.forEach(p => {
            if (p.CalendarEventId && p.AvailabilityCalendarColor && !map[p.CalendarEventId]) {
                map[p.CalendarEventId] = p.AvailabilityCalendarColor;
            }
        });
        return map;
    }, [data?.HotelParameters]);

    // OCCROOMS-1 perf: compute date-bucket Maps and event-span centers ONCE per
    // data change, then hand them to every DayCell. This replaces the previous
    // per-cell linear scans (Yield/Availability/Overbooking/Products/Restrictions/
    // ReservationPolicies/HotelParameters/Forecast/HotelAvailability) and the
    // O(n) center-walk that ran per event-bearing cell.
    const dateBuckets = useDayCellBuckets(data);
    const eventSpans = useEventSpans(data?.HotelParameters);
    const allocationDateBuckets = useAllocationDateBuckets(allocationDates, allocationIds);

    // HS-70186: grid-only - Cells indexed by ISO date for O(1) lookup in dayCellContent.
    const gridCellsByDate = useMemo(() => {
        const map = new Map();
        (gridResult?.Cells || []).forEach(c => map.set(moment(c.Date).format('YYYY-MM-DD'), c));
        return map;
    }, [gridResult]);

    const dayCellContent = (info) => {
        if (flow === 'grid') {
            const dateKey = moment(info.date).format('YYYY-MM-DD');
            const cell = gridCellsByDate.get(dateKey);
            return (
                <GridDayCell
                    date={info.date}
                    cell={cell}
                    policy={cell?.PolicyId != null ? gridResult?.Policies?.[cell.PolicyId] : null}
                    allocationDates={allocationDateBuckets.get(dateKey)}
                    text={text}
                    message={message}
                    currentHotel={currentHotel}
                    availableMode={availableMode}
                    roomTypeName={roomTypeLineName(gridRoomTypeId, roomTypeId, roomTypeName)}
                    afterTax={pricesIncludeTax || afterTax}
                    showPrices={!!gridResult?.ShowPrices}
                    selected={selectedDate && info.date.toDateString() === selectedDate.toDateString()}
                    onSelect={setSelectedDate}
                />
            );
        }
        // Legacy: exact current code path, untouched.
        return (
            <DayCell
                date={info.date}
                data={data}
                dateBuckets={dateBuckets}
                eventSpans={eventSpans}
                allocationDateBuckets={allocationDateBuckets}
                state={state}
                allocationDates={allocationDates}
                allocationIds={allocationIds}
                text={text}
                message={message}
                currentHotel={currentHotel}
                availableMode={availableMode}
                afterTax={pricesIncludeTax || afterTax}
                eventColorMap={eventColorMap}
                selected={selectedDate && info.date.toDateString() === selectedDate.toDateString()}
                onSelect={setSelectedDate}
            />
        );
    }

    const onSearchComplete = (responseData, responseState) => {
        if (flow === 'grid') {
            // Drop a late response from a superseded month/filter change instead of
            // overwriting newer data (see latestGridSpecRef).
            if (responseState !== latestGridSpecRef.current) {
                return;
            }
            setGridResult(responseData)
            setGridRoomTypeId(responseState?.RoomTypeId ?? null)
            setState(responseState)
            return;
        }
        setData(responseData)
        setState(responseState)
    }

    const activeFilterChips = useMemo(() => {
        const chips = [];
        if (hotelId) {
            const hotelName = currentHotel?.Name;
            if (hotelName) chips.push({ label: text("Hotel"), value: hotelName });
        }
        if (roomTypeName) chips.push({ label: text("RoomType"), value: roomTypeName });
        if (rateName) chips.push({ label: text("Rate"), value: rateName });
        if (packageName) chips.push({ label: text("Package") || "Package", value: packageName });
        if (availableMode) {
            chips.push({ label: "", value: text("Available") || "Available" });
        } else {
            chips.push({ label: "", value: text("Occupied") || "Occupied" });
        }
        if (allocationIds.length > 0) chips.push({ label: "", value: text("Allocation") });
        return chips;
    }, [hotelId, currentHotel, roomTypeName, rateName, packageName, availableMode, allocationIds, text]);

    const userLang = window.optima?.user?.UserSettings[0]?.Language?.LocaleID;

    return <div className="monthlyviewcontainer" ref={containerRef} style={containerHeight ? { height: containerHeight } : {}}>
        <div className="sidemenu">
            <MonthlySideMenu onSavePopup={() => doSearchRef.current?.()} selectedDate={selectedDate} />
        </div>
        <div className="monthlyview">
            <div className="filter-toggle" role="button" tabIndex={0} onClick={() => setFilterExpanded(!filterExpanded)} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setFilterExpanded(!filterExpanded); } }} aria-expanded={filterExpanded}>
                <Icon name={filterExpanded ? 'chevron up' : 'filter'} />
                <span>{text("Search")}</span>
                {!filterExpanded && <FilterChips filters={activeFilterChips} />}
            </div>
            <div className="filter" style={filterExpanded ? {} : { height: 0, overflow: 'hidden', margin: 0, padding: 0 }}>
                <SearchPanel options={options} onSearchComplete={onSearchComplete} />
            </div>
            <div className="calendar-with-summary">
                <div className="calendar">
                    <FullCalendar
                        schedulerLicenseKey="GPL-My-Project-Is-Open-Source"
                        locale={userLang || document.documentElement.lang || "en"}
                        plugins={[dayGridPlugin]}
                        height="100%"
                        datesSet={datesRender}
                        dayCellContent={dayCellContent}
                        fixedWeekCount={false}
                        initialView="dayGridMonth"
                        headerToolbar={{
                            left: 'title prev,next',
                            center: '',
                            right: 'today'
                        }}
                        buttonText={{
                            today: text("Today")
                        }}
                    />
                </div>
            </div>
        </div>
    </div>
}
